import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ShieldCheck } from 'lucide-react';
import { getToken } from '../lib/api';
import { Dialog } from '../components/Dialog';

interface AllowlistEntry {
  id: number;
  domain_uuid: string;
  cidr: string;
  note: string | null;
  created_at: string;
}

const CIDR_RE = /^(\d{1,3}\.){3}\d{1,3}(\/(\d|[12]\d|3[0-2]))?$/;

function validateCidr(v: string): string | null {
  const t = v.trim();
  if (!t) return 'CIDR is required';
  if (!CIDR_RE.test(t)) return 'Enter a valid IPv4 address or CIDR range (e.g. 192.168.1.0/24)';
  const parts = t.split('/')[0].split('.').map(Number);
  if (parts.some(p => p > 255)) return 'IP address octets must be 0–255';
  return null;
}

export default function IpAllowlist() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ cidr: '', note: '' });
  const [cidrError, setCidrError] = useState<string | null>(null);

  const { data: list = [] } = useQuery<AllowlistEntry[]>({
    queryKey: ['tenant-ip-allowlist'],
    queryFn: async () =>
      fetch('/api/tenant-ip-allowlist', {
        headers: { Authorization: `Bearer ${getToken()}` },
      }).then(r => r.json()),
  });

  const create = useMutation({
    mutationFn: async () => {
      const err = validateCidr(form.cidr);
      if (err) { setCidrError(err); throw new Error(err); }
      const r = await fetch('/api/tenant-ip-allowlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ cidr: form.cidr.trim(), note: form.note.trim() || undefined }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || r.statusText);
      }
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-ip-allowlist'] });
      setOpen(false);
      setForm({ cidr: '', note: '' });
      setCidrError(null);
    },
  });

  const remove = useMutation({
    mutationFn: async (id: number) =>
      fetch(`/api/tenant-ip-allowlist/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-ip-allowlist'] }),
  });

  const handleOpen = () => { setForm({ cidr: '', note: '' }); setCidrError(null); setOpen(true); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">IP Allowlist</h1>
        <button onClick={handleOpen} className="btn-primary inline-flex items-center gap-2">
          <Plus size={14} /> Add CIDR
        </button>
      </div>

      <p className="text-sm text-textSecondary">
        Restrict portal and admin logins for this department to specific IP addresses or ranges.
        When the list is empty, logins are permitted from any IP address.
      </p>

      {list.length === 0 ? (
        <div className="card flex flex-col items-center gap-3 py-10 text-center">
          <ShieldCheck size={32} className="text-textSecondary opacity-50" />
          <p className="text-sm text-textSecondary">
            No restrictions — logins are allowed from any IP address.
          </p>
          <p className="text-xs text-textSecondary">
            Add a CIDR entry to lock logins to specific networks.
          </p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th>CIDR Range</th>
                <th>Note</th>
                <th>Added</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map(entry => (
                <tr key={entry.id} className="hover:bg-elevated">
                  <td className="font-mono">{entry.cidr}</td>
                  <td className="text-textSecondary">{entry.note || '—'}</td>
                  <td className="text-xs text-textSecondary">
                    {new Date(entry.created_at).toLocaleString()}
                  </td>
                  <td>
                    <button
                      onClick={() => remove.mutate(entry.id)}
                      className="btn-ghost p-1 text-danger"
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Add allowed IP range"
        size="md"
        footer={
          <>
            <button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
            <button onClick={() => create.mutate()} className="btn-primary" disabled={create.isPending}>
              Add
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-textSecondary mb-1">
              IP Address or CIDR Range *
            </label>
            <input
              className={`input font-mono${cidrError ? ' border-danger' : ''}`}
              placeholder="192.168.1.0/24"
              value={form.cidr}
              onChange={e => { setForm({ ...form, cidr: e.target.value }); setCidrError(null); }}
            />
            {cidrError && (
              <p className="mt-1 text-xs text-danger">{cidrError}</p>
            )}
          </div>
          <div>
            <label className="block text-sm text-textSecondary mb-1">Note (optional)</label>
            <input
              className="input"
              placeholder="e.g. Office network"
              value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
            />
          </div>
          {create.isError && (
            <p className="text-xs text-danger">{(create.error as Error).message}</p>
          )}
        </div>
      </Dialog>
    </div>
  );
}
